import { ChangeDetectorRef, ComponentRef } from '@angular/core';
import { Subject, Observable } from 'rxjs';
import { isFunction } from 'lodash';

import { ModalComponent } from '@app/modules/modal-components-common/modal.component';

import type { ModalCommand } from '../types/modal.types';

type Constructor<T> = Function & { prototype: T };

export class ModalRef<T = any> {
  public commands$: Subject<ModalCommand<T>> = new Subject();
  private changeDetectorRef: ChangeDetectorRef;

  public constructor(private componentRef: ComponentRef<ModalComponent>) {
    this.changeDetectorRef = componentRef.injector.get(ChangeDetectorRef);
  }

  public updateData(data: Object = {}) {
    Object.assign(this.componentRef.instance, data);
    this.changeDetectorRef.detectChanges();
  }

  public result(): Observable<ModalCommand<T>> {
    return this.commands$.asObservable();
  }

  public promiseResult(): Promise<ModalCommand<T>> {
    return this.commands$.toPromise();
  }

  public executeAndClose(command?: ModalCommand): void {
    this.commands$.next(command);
    if (!command || !command.hasToStayOpenAfterExecution) {
      this.commands$.complete();
      this.removeModalElement();
    }
  }

  public dismiss(reason?: any): void {
    this.commands$.error(reason);
    this.commands$.complete();
    this.removeModalElement();
  }

  public isInstanceOf<P extends Constructor<{}>>(modalType: P): boolean {
    return (
      isFunction(modalType) && this.componentRef.instance instanceof modalType
    );
  }

  public getModalInstance(): any {
    return this.componentRef.instance;
  }

  public getModalName(): string {
    return this.componentRef?.componentType?.name;
  }

  public detectChanges() {
    this.changeDetectorRef.detectChanges();
  }

  private removeModalElement(): void {
    if (this.componentRef) {
      this.componentRef.destroy();
      this.changeDetectorRef.detach();
    }
  }
}
